import { useEffect, useState } from "react";
import {
  List,
  TextField,
  Datagrid,
  ListActions,
  DateField,
  ReferenceField,
  ShowButton,
  useStore,
  useGetList,
  FunctionField,
  Title,
  BooleanField,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  NumberInput,
  SimpleList,
} from "react-admin";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import { settings } from "app";
import { administrationRequests } from "api/administrationApi";
import SecuredComponent from "server/security/SecuredComponent";
import CustomFilter from "features/CustomFilter";
import { ALL_OPTIONS } from "features/filters/constants";
import { studentRequests } from "api/studentApi";
import { CourseChoices } from "utils/choices";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { MobileListRow } from "features/MobileListRow";
import GenerateExamProtocolButton from "./GenerateExamProtocolButton";
import AddStudentsToSessionExamDialog from "./AddStudentsToSessionExamDialog";
export const SESSION_EXAMS_KEY = "sessionExams.filters";

const ListSessionExams = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const userPermissions = getPermissions();

  const [filters, setFilters] = useStore(SESSION_EXAMS_KEY, null);

  // Pre-filter draft, committed to `filters` on "Покажи".
  const [academicYearId, setAcademicYearId] = useState(filters?.academicYearId ?? null);
  const [structuralUnitId, setStructuralUnitId] = useState(filters?.structuralUnitId ?? null);
  const [instructionLanguageId, setInstructionLanguageId] = useState(filters?.instructionLanguageId ?? null);
  const [academicYearSessionId, setAcademicYearSessionId] = useState(filters?.academicYearSessionId ?? null);

  const { data: academicYears } = useGetList(studentRequests.getAcademicYearsSelect(), ALL_OPTIONS);
  const { data: structuralUnits } = useGetList(administrationRequests.getStructuralUnitsSelect(), ALL_OPTIONS);
  const { data: languages } = useGetList(administrationRequests.getInstructionLanguagesSelect(), ALL_OPTIONS);
  const { data: sessions } = useGetList(
    studentRequests.getAcademicSessionsSelect(),
    { ...ALL_OPTIONS, filter: { onlySessions: true, academicYearId, instructionLanguageId, structuralUnitId } },
    { enabled: Boolean(academicYearId && structuralUnitId && instructionLanguageId) },
  );

  useEffect(() => {
    if (!academicYearId && academicYears?.length) setAcademicYearId(academicYears[0].id);
  }, [academicYearId, academicYears]);

  const changeYear = value => {
    setAcademicYearId(value);
    setStructuralUnitId(null);
    setInstructionLanguageId(null);
    setAcademicYearSessionId(null);
  };
  const changeStructuralUnit = value => {
    setStructuralUnitId(value);
    setInstructionLanguageId(null);
    setAcademicYearSessionId(null);
  };
  const changeLanguage = value => {
    setInstructionLanguageId(value);
    setAcademicYearSessionId(null);
  };

  // Create reads the session type from the committed filters.
  const onSearch = () => {
    const session = sessions?.find(option => String(option.id) === String(academicYearSessionId));
    handleSearch({
      academicYearId,
      structuralUnitId,
      instructionLanguageId,
      academicYearSessionId,
      academicYearSessionType: session?.sessionType ?? "",
    });
  };

  const handleSearch = selectedFilters => {
    setFilters(selectedFilters);
  };

  const datagridFilters = [
    <SelectInput
      source="course"
      label="Курс"
      choices={CourseChoices}
      optionValue="id"
      optionText="name"
      alwaysOn
      emptyText={"Всички"}
    />,
    <ReferenceInput
      source="disciplineId"
      reference={administrationRequests.getDisciplinesSelect()}
      label="Дисциплина"
      filter={{ filterByUser: true }}
      alwaysOn
    >
      <AutocompleteInput
        optionValue="id"
        optionText="name"
        label="Дисциплина"
        filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
        sx={{ width: "300px" }}
      />
    </ReferenceInput>,
    <CustomDateInput
      label="Дата на изпит"
      source="examDate"
      noTime
      alwaysOn
    />,
    <NumberInput
      label="Учебна група"
      source="group"
      min={1}
      alwaysOn
      fullWidth
    />,
  ];

  return (
    <>
      {!filters && <Title title={"Сесийни изпити"} />}
      <CustomFilter
        title="Избери сесийни изпити"
        cardWidth={isMobile ? "100%" : "700px"}
        filters={[
          {
            label: "Академична година",
            type: "select",
            value: academicYearId ?? "",
            setValue: changeYear,
            choices: academicYears ?? [],
          },
          {
            label: "Факултет",
            type: "select",
            value: structuralUnitId ?? "",
            setValue: changeStructuralUnit,
            choices: structuralUnits ?? [],
          },
          {
            label: "Език",
            type: "select",
            value: instructionLanguageId ?? "",
            setValue: changeLanguage,
            choices: languages ?? [],
          },
          ...(sessions?.length
            ? [
                {
                  label: "Сесия",
                  type: "select",
                  value: academicYearSessionId ?? "",
                  setValue: setAcademicYearSessionId,
                  choices: sessions,
                  optionText: "sessionTypeText",
                },
              ]
            : []),
        ]}
        buttonLabel="Покажи"
        buttonDisabled={!academicYearId || !structuralUnitId || !instructionLanguageId || !academicYearSessionId}
        buttonOnClick={onSearch}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{ display: "flex", justifyContent: "center", gap: 3 }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за да се покаже списък с изпити или да създадеш нови.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <List
          exporter={false}
          title="Сесийни изпити"
          filter={filters}
          filters={datagridFilters}
          actions={<ListActions hasCreate={checkPermission(userPermissions, permissions.canCreateSessionExams)} />}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Дисциплина",
                        value: (
                          <ReferenceField
                            source="disciplineId"
                            reference={administrationRequests.getDisciplinesSelect()}
                            sortable={false}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Дата на изпит",
                        value: (
                          <DateField
                            source="examDate"
                            locales="bg-BG"
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Начало",
                        value: record?.examStartTime ? `${record.examStartTime.substring(0, 5)} ч.` : " - ",
                      },
                      {
                        label: "Край",
                        value: record?.examEndTime ? `${record.examEndTime.substring(0, 5)} ч.` : " - ",
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Зала",
                        value: (
                          <ReferenceField
                            source="roomId"
                            reference={administrationRequests.getRoomsSelect()}
                            sortable={false}
                          >
                            <TextField source="roomName" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Курс", value: record?.courses?.join(", ") }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Учебни групи",
                        value: record?.allStudyGroups ? "ВСИЧКИ" : record?.studyGroups?.join(", "),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Положен изпит", value: record?.examPassed ? "Да" : "Не" }]} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <SecuredComponent permission={permissions.canPreviewSessionExams}>
                      <ShowButton />
                    </SecuredComponent>
                    <SecuredComponent permission={permissions.canAddStudentsToSessionExam}>
                      <AddStudentsToSessionExamDialog
                        sessionExamId={record.id}
                        examDate={record.examDate}
                        examStartTime={record.examStartTime}
                      />
                    </SecuredComponent>
                    <SecuredComponent permission={permissions.canPreviewSessionExams}>
                      <GenerateExamProtocolButton sessionExamId={record.id} />
                    </SecuredComponent>
                  </div>
                </div>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
            >
              <ReferenceField
                source="disciplineId"
                reference={administrationRequests.getDisciplinesSelect()}
                label="Дисциплина"
              >
                <TextField source="name" />
              </ReferenceField>
              <DateField
                label="Дата на изпит"
                source="examDate"
                locales="bg-BG"
              />
              <FunctionField
                label="Начален час на изпит"
                render={record => (record?.examStartTime ? `${record?.examStartTime.substring(0, 5)} ч.` : " - ")}
              />
              <FunctionField
                label="Краен час на изпит"
                render={record => (record?.examEndTime ? `${record?.examEndTime.substring(0, 5)} ч.` : " - ")}
              />
              <ReferenceField
                source="roomId"
                reference={administrationRequests.getRoomsSelect()}
                label="Зала"
              >
                <TextField source="roomName" />
              </ReferenceField>
              <BooleanField
                source="examPassed"
                label="Положен изпит?"
              />
              <FunctionField
                label="Курс"
                render={rec => rec?.courses?.join(", ")}
              />
              <FunctionField
                label="Учебни групи"
                render={rec => (rec?.allStudyGroups ? "ВСИЧКИ" : rec?.studyGroups?.join(", "))}
              />

              {/* <SecuredComponent permission={permissions.canUpdateSessionExamProfessors}>
                <FunctionField
                  render={rec => (
                    <Button
                      startIcon={<GroupsIcon />}
                      label="Преподаватели"
                      onClick={() => redirect(`/${StudentServicePath}${SESSION_EXAMS_PROFESSORS}/${rec.id}/edit`)}
                    />
                  )}
                />
              </SecuredComponent> */}
              <SecuredComponent permission={permissions.canPreviewSessionExams}>
                <ShowButton />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canAddStudentsToSessionExam}>
                <FunctionField
                  render={rec => (
                    <AddStudentsToSessionExamDialog
                      sessionExamId={rec.id}
                      examDate={rec.examDate}
                      examStartTime={rec.examStartTime}
                    />
                  )}
                />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canPreviewSessionExams}>
                <FunctionField render={rec => <GenerateExamProtocolButton sessionExamId={rec.id} />} />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};
export default ListSessionExams;
